/**
 * R232q28 RepairDialog — opens on `aiwari-open-repair` window event.
 *
 * Flow:
 *   1. ServerNpcDialog dispatches `aiwari-open-repair { npcId }` after the
 *      player picks the "Почини снаряжение" option.
 *   2. We send `repair-quote` to the room. Server replies `repair-quote-result`
 *      with the list of equipped items + per-item repair cost + total gold.
 *   3. Render parchment-style modal with NPC name, durability bars and the
 *      grand-total cost. Two action buttons: «Починить всё» / «Закрыть».
 *   4. "Repair All" sends `repair-pay { all: true }` → server replies
 *      `repair-result { ok, costGold, totalGold, repairedSlots }`.
 *
 * Visual: matches VendorShop / ServerNpcDialog heraldic theme — Cinzel font,
 * gold-on-dark parchment, rarity-tinted item tiles, subtle gold ornaments.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRoom } from '../game/realmClient.js';
import { useNet } from '../state/net-store.js';
import { ITEMS, RARITY_COLOUR } from '../data/equipment.js';

interface OpenRepairEvent {
  npcId: string;
}

interface QuoteItem {
  slot: string;
  itemId: string;
  durability: number;
  cost: number;
}

interface QuoteResult {
  ok: boolean;
  items?: QuoteItem[];
  totalCost?: number;
  totalGold?: number;
}

interface RepairResult {
  ok: boolean;
  reason?: string;
  costGold?: number;
  totalGold?: number;
  repairedSlots?: string[];
}

const SLOT_RU: Record<string, string> = {
  head: 'Голова',
  shoulders: 'Плечи',
  chest: 'Грудь',
  legs: 'Ноги',
  feet: 'Ступни',
  mainhand: 'Основная рука',
  offhand: 'Вторая рука',
  cape: 'Плащ',
};

function durabilityColor(dur: number): string {
  if (dur <= 0) return '#7F1D1D';        // broken: deep red
  if (dur < 30) return '#DC2626';        // critical
  if (dur < 60) return '#F59E0B';        // warn
  return '#65A30D';                      // healthy
}

export function RepairDialog() {
  const [open, setOpen] = useState<string | null>(null);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const npcs = useNet((s) => s.npcs);

  // Subscribe to the `aiwari-open-repair` window event.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenRepairEvent>).detail;
      if (!detail?.npcId) return;
      setOpen(detail.npcId);
      setQuote(null);
      setToast(null);
    };
    window.addEventListener('aiwari-open-repair', handler as EventListener);
    return () => window.removeEventListener('aiwari-open-repair', handler as EventListener);
  }, []);

  // When a session opens, request a quote and listen for replies.
  useEffect(() => {
    if (!open) return;
    const room = getRoom();
    if (!room) {
      setToast('Нет соединения с сервером');
      return;
    }
    const offQuote = room.onMessage('repair-quote-result', (msg: QuoteResult) => {
      setQuote(msg);
    });
    const offRepair = room.onMessage('repair-result', (msg: RepairResult) => {
      setBusy(false);
      if (msg.ok) {
        setToast(`Починено за ${msg.costGold ?? 0} ✦ — осталось ${msg.totalGold ?? 0} ✦`);
        // Refresh quote so durability bars reset to 100.
        try { room.send('repair-quote'); } catch { /* ignore */ }
      } else {
        const reasonRu = msg.reason === 'no-repair-npc-nearby'
          ? 'Кузнеца нет рядом'
          : msg.reason === 'insufficient-funds'
            ? `Не хватает средств (нужно ${msg.costGold ?? 0} ✦)`
            : msg.reason === 'nothing-to-repair'
              ? 'Всё снаряжение в порядке'
              : `Ошибка: ${msg.reason ?? 'неизвестно'}`;
        setToast(reasonRu);
      }
      setTimeout(() => setToast(null), 3000);
    });
    try { room.send('repair-quote'); } catch { /* ignore */ }
    return () => {
      try { offQuote(); } catch { /* ignore */ }
      try { offRepair(); } catch { /* ignore */ }
    };
  }, [open]);

  const close = useCallback(() => {
    setOpen(null);
    setQuote(null);
    setToast(null);
    setBusy(false);
  }, []);

  const repairAll = useCallback(() => {
    const room = getRoom();
    if (!room) {
      setToast('Нет соединения с сервером');
      return;
    }
    setBusy(true);
    try { room.send('repair-pay', { all: true }); } catch {
      setBusy(false);
      setToast('Не удалось отправить запрос');
    }
  }, []);

  const npc = open ? npcs[open] : undefined;
  const items = quote?.items ?? [];
  const totalCost = quote?.totalCost ?? 0;
  const totalGold = quote?.totalGold ?? 0;
  const canPay = totalCost > 0 && totalGold >= totalCost && !busy;

  const damaged = useMemo(() => items.filter((i) => i.durability < 100), [items]);

  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="rounded-2xl p-6 max-w-[680px] w-[94vw] max-h-[88vh] flex flex-col gap-4"
        style={{
          background: 'linear-gradient(180deg, rgba(20,15,8,0.96), rgba(8,5,3,0.96))',
          border: '2px solid #C9A96A',
          boxShadow: '0 0 60px rgba(251,191,36,0.4)',
          fontFamily: 'system-ui',
        }}
      >
        {/* Header */}
        <div
          className="flex gap-4 items-start pb-3"
          style={{ borderBottom: '1px solid rgba(251,191,36,0.3)' }}
        >
          <div
            className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center text-3xl"
            style={{ border: '2px solid #C9A96A', boxShadow: '0 0 18px rgba(251,191,36,0.55)', background: 'rgba(0,0,0,0.5)' }}
          >
            ⚒
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.4em] uppercase" style={{ color: '#C9A96A' }}>
              Кузнец-починщик
            </div>
            <h2
              className="text-2xl font-bold"
              style={{ color: '#E8C77B', fontFamily: 'Cinzel, serif' }}
            >
              {npc?.displayName ?? 'Кузнец'}
            </h2>
            <div
              className="text-[13px] italic mt-1"
              style={{ color: '#E8DCC0', opacity: 0.8, fontFamily: 'Cormorant Garamond, serif', fontSize: 14 }}
            >
              «Положи снаряжение на наковальню. Починю — недорого.»
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: '#C9A96A' }}>Кошелёк</div>
            <div className="text-2xl font-bold" style={{ color: '#E8C77B' }}>{totalGold.toLocaleString()} ✦</div>
            <button
              onClick={close}
              className="text-xl mt-1"
              style={{ color: '#C9A96A' }}
              type="button"
            >×</button>
          </div>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto pr-1">
          <div
            className="text-[11px] uppercase tracking-wider mb-2"
            style={{ color: '#C9A96A', fontFamily: 'Cinzel, serif' }}
          >
            Экипировка ({items.length} {items.length === 1 ? 'предмет' : 'предметов'})
          </div>

          {items.length === 0 && (
            <div className="text-center py-8 text-[13px]" style={{ color: '#E8DCC0', opacity: 0.7 }}>
              {quote ? 'Снаряжение не надето.' : 'Загрузка…'}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            {items.map((it) => {
              const def = ITEMS.find((i) => i.id === it.itemId);
              const accent = def ? RARITY_COLOUR[def.rarity] : '#C9A96A';
              const pct = Math.max(0, Math.min(100, it.durability));
              const barColor = durabilityColor(it.durability);
              const slotLabel = SLOT_RU[it.slot] ?? it.slot;
              return (
                <div
                  key={it.slot}
                  className="rounded-md p-2 flex items-center gap-3"
                  style={{
                    background: `linear-gradient(135deg, ${accent}18, rgba(15,11,7,0.95))`,
                    border: `1px solid ${accent}80`,
                  }}
                >
                  <div className="flex-shrink-0 w-16 text-[10px] uppercase tracking-wider" style={{ color: '#C9A96A' }}>
                    {slotLabel}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[12px] font-bold truncate"
                      style={{ color: accent, fontFamily: 'Cinzel, serif' }}
                    >
                      {def?.name.ru ?? it.itemId}
                    </div>
                    <div
                      className="h-2 rounded-full mt-1 overflow-hidden"
                      style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(201,169,106,0.3)' }}
                    >
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, background: barColor }}
                      />
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-[11px] font-mono" style={{ color: '#E8DCC0' }}>
                      {it.durability}/100
                    </div>
                    <div className="text-[11px] font-bold" style={{ color: it.cost > 0 ? '#E8C77B' : '#65A30D' }}>
                      {it.cost > 0 ? `${it.cost} ✦` : 'OK'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {damaged.length > 0 && (
            <div
              className="mt-3 p-2.5 rounded-md flex items-center justify-between"
              style={{ background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.35)' }}
            >
              <div className="text-[12px]" style={{ color: '#E8DCC0' }}>
                Итого ремонт ({damaged.length} {damaged.length === 1 ? 'предмета' : 'предметов'}):
              </div>
              <div className="text-[16px] font-bold" style={{ color: '#E8C77B' }}>
                {totalCost.toLocaleString()} ✦
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={repairAll}
            disabled={!canPay}
            className="flex-1 py-2.5 rounded-md font-bold transition"
            style={{
              background: canPay ? 'linear-gradient(180deg, #C9A96A, #8B7345)' : 'rgba(80,70,55,0.5)',
              color: canPay ? '#1A130A' : '#8B7345',
              border: '1px solid #C9A96A',
              fontFamily: 'Cinzel, serif',
              cursor: canPay ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Идёт ремонт…' : damaged.length === 0 ? 'Ничего чинить' : `Починить всё — ${totalCost} ✦`}
          </button>
          <button
            type="button"
            onClick={close}
            className="py-2.5 px-4 rounded-md transition"
            style={{
              background: 'rgba(40,30,20,0.6)',
              color: '#E8DCC0',
              border: '1px solid rgba(201,169,106,0.5)',
              fontFamily: 'Cinzel, serif',
            }}
          >
            Закрыть
          </button>
        </div>

        {toast && (
          <div
            className="text-center py-2 px-4 rounded-md text-[12px] font-bold"
            style={{
              background: 'rgba(251,191,36,0.15)',
              border: '1px solid rgba(251,191,36,0.5)',
              color: '#FED7AA',
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}
